import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { GH_COLORS } from "./Style";

interface DecodedToken {
  username: string;
  sub: number;
  role: "admin" | "teacher" | "viewer";
  iat: number;
  exp: number;
}

function decodeJWT(token: string): DecodedToken | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const decoded = JSON.parse(atob(parts[1]));
    return decoded as DecodedToken;
  } catch (e) {
    return null;
  }
}

interface NavItem {
  label: string;
  path: string;
  icon: string;
}

interface NavSection {
  category: string;
  items: NavItem[];
}

export default function SideBar({
  activeCategory,
}: {
  activeCategory: string;
}) {
  const [role, setRole] = useState<"admin" | "teacher" | "viewer" | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();

  const EXPANDED_WIDTH = 170;
  const COLLAPSED_WIDTH = 64;
  const currentWidth = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  // JWT 토큰에서 role 확인
  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      const decoded = decodeJWT(token);
      if (decoded) {
        setRole(decoded.role);
      }
    }
  }, []);

  // 라우트 변경 시 체크
  const isAuthRoute = location.pathname.startsWith("/login") || location.pathname.startsWith("/auth/");
  if (isAuthRoute) return null;
  if (!role || (role !== "admin" && role !== "teacher")) return null;

  // 카테고리별 하위 항목 정의
  const navSections: { [key: string]: NavSection } = {
    student: {
      category: "학생 관리",
      items: [
        { label: "학생 관리", path: "/student", icon: "👥" },
        { label: "학생 관리 이력", path: "/history/students", icon: "📜" },
      ],
    },
    classroom: {
      category: "수업 관리",
      items: [
        { label: "정규반 관리", path: "/class/regular", icon: "📚" },
        { label: "특강반 관리", path: "/class/special", icon: "⭐" },
        { label: "보강반 관리", path: "/class/supplement", icon: "🔧" },
        { label: "시간표", path: "/schedule", icon: "🕐" },
        { label: "시간표 일람", path: "/schedule/all", icon: "📅" },
      ],
    },
    attendance: {
      category: "출결 관리",
      items: [
        { label: "결석 보상 관리", path: "/compensation", icon: "💰" },
        { label: "출결 수정", path: "/attendance/edit", icon: "✏️" },
        { label: "출결 이력", path: "/attendance", icon: "🚪" },
      ],
    },
    message: {
      category: "메시지 관리",
      items: [
        { label: "메시지 보내기", path: "/message", icon: "💬" },
        { label: "템플릿 관리", path: "/message/template", icon: "📝" },
        { label: "메시지 이력", path: "/message/history", icon: "📨" },
        { label: "알림톡 이력", path: "/message/alimtalk-history", icon: "🔔" },
      ],
    },
    point: {
      category: "포인트 관리",
      items: [
        { label: "포인트 관리", path: "/point", icon: "💳" },
        { label: "포인트 이력", path: "/history/points", icon: "📜" },
        { label: "포인트 설정", path: "/point/settings", icon: "⚙️" },
      ],
    },
    settings: {
      category: "설정",
      items: [
        { label: "기본 설정", path: "/settings", icon: "⚙️" },
        { label: "알림톡 템플릿 관리", path: "/settings/alimtalk-templates", icon: "📋" },
        ...(role === "admin" ? [{ label: "사용자 관리", path: "/users", icon: "🔐" }] : []),
        ...(role === "admin" ? [{ label: "권한 설정", path: "/settings/permissions", icon: "🔑" }] : []),
      ],
    },
  };

  const currentSection = navSections[activeCategory];

  if (!currentSection) {
    return null; // activeCategory가 없으면 사이드바 숨김
  }

  const linkStyleBase: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 12px",
    paddingLeft: "9px",
    borderLeft: `3px solid transparent`,
    color: "#ffffff",
    fontSize: 14,
    fontWeight: 500,
    textDecoration: "none",
    borderRadius: 6,
    whiteSpace: "nowrap",
    overflow: "hidden",
    transition: "all 0.2s",
    userSelect: "none",
    WebkitUserSelect: "none",
  };

  const activeLinkStyle: React.CSSProperties = {
    ...linkStyleBase,
    backgroundColor: "rgba(79, 171, 255, 0.3)",
    borderLeft: `3px solid ${GH_COLORS.btnPrimaryBg}`,
    paddingLeft: "9px",
    fontWeight: 600,
  };

  const labelStyle: React.CSSProperties = {
    color: "#ffffff",
    display: collapsed ? "none" : "inline",
  };

  return (
    <aside
      aria-label="사이드 메뉴"
      aria-expanded={!collapsed}
      style={{
        width: currentWidth,
        userSelect: "none",
        WebkitUserSelect: "none",
        height: "100%",
        padding: "12px 8px",
        background: "#24292f",
        boxShadow: "2px 0 6px rgba(0,0,0,0.06)",
        boxSizing: "border-box",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        overflowY: "auto",
        flexShrink: 0,
        transition: "width 180ms ease",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-end",
          gap: 8,
        }}
      >
        <button
          onClick={() => setCollapsed((s) => !s)}
          aria-label={collapsed ? "사이드바 확장" : "사이드바 축소"}
          title={collapsed ? "확장" : "축소"}
          style={{
            width: 32,
            height: 32,
            borderRadius: 6,
            border: "1px solid rgba(255,255,255,0.2)",
            background: "rgba(255,255,255,0.12)",
            boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            color: "#ffffff",
          }}
        >
          {collapsed ? "▶" : "◀"}
        </button>
      </div>

      {/* 카테고리 제목 */}
      <div
        style={{
          display: collapsed ? "none" : "block",
          padding: "8px 12px",
          color: "rgba(255, 255, 255, 0.7)",
          fontSize: 12,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {currentSection.category}
      </div>

      <nav style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {currentSection.items.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              style={isActive ? activeLinkStyle : linkStyleBase}
              title={item.label}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              <span aria-hidden style={{ minWidth: 28, textAlign: "center" }}>
                {item.icon}
              </span>
              <span style={labelStyle}>{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}
